import React, { useState } from 'react';
import { useStateValue } from '../StateProvider';
import { stripeConfig, subscriptionTiers } from '../stripeConfig';
import './SubscriptionManager.css';

function SubscriptionManager() {
  const [{ userTier, userEmail, subscriptionStatus, subscriptionConfig }] = useStateValue();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!userEmail) {
        setError('Please log in with Spotify first');
        setLoading(false);
        return;
      }

      console.log('🔵 Starting subscription for:', userEmail);
      console.log('🔵 Price ID:', stripeConfig.priceId);
      console.log('🔵 Success URL:', stripeConfig.successUrl);
      console.log('🔵 Cancel URL:', stripeConfig.cancelUrl);

      // Call Firebase Cloud Function to create checkout session
      const response = await fetch(
        `https://us-central1-spot-feedback-carousel.cloudfunctions.net/createCheckoutSession`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userEmail,
            priceId: stripeConfig.priceId,
            successUrl: stripeConfig.successUrl,
            cancelUrl: stripeConfig.cancelUrl,
          }),
        }
      );

      console.log('🔵 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('🔴 Server error:', errorData);
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();
      console.log('🔵 Checkout session created:', data);

      if (!data.url) {
        throw new Error('No checkout URL returned from server');
      }

      // Redirect directly to Stripe Checkout URL
      console.log('🔵 Redirecting to Stripe Checkout:', data.url);
      window.location.href = data.url;
    } catch (err) {
      console.error('🔴 Subscription error:', err);
      setError(err.message || 'Failed to start subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!userEmail) {
        setError('Please log in with Spotify first');
        setLoading(false);
        return;
      }

      // Call Firebase Cloud Function to create portal session
      const response = await fetch(
        `https://us-central1-spot-feedback-carousel.cloudfunctions.net/createPortalSession`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userEmail,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Customer Portal
      window.location.href = url;
    } catch (err) {
      console.error('Portal error:', err);
      setError('Failed to open subscription management. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="subscription-manager">
      <div className="subscription-header">
        <h2>
          {userTier === 'premium' ? '👑 Premium Member' : '🎵 Free Member'}
        </h2>
        <p className="subscription-status">
          Status: <span className={`status-badge ${subscriptionStatus}`}>
            {subscriptionStatus === 'active' ? 'Active' :
             subscriptionStatus === 'past_due' ? 'Payment Required' :
             subscriptionStatus === 'canceled' ? 'Canceled' : 'Free'}
          </span>
        </p>
      </div>

      {userTier === 'free' ? (
        <div className="free-tier-section">
          <h3>Upgrade to Premium</h3>

          <div className="comparison-table">
            <div className="tier-column free">
              <h4>Free Tier</h4>
              <ul>
                <li>❌ Wait {subscriptionTiers.free.listeningTimeRequired} seconds to rate</li>
                <li>❌ See only some ratings</li>
                <li>❌ Upload after rating {subscriptionTiers.free.songsToRateBeforeUpload} songs</li>
              </ul>
            </div>

            <div className="tier-column premium">
              <h4>Premium Tier</h4>
              <ul>
                <li>✅ Rate after only {subscriptionTiers.premium.listeningTimeRequired} seconds</li>
                <li>✅ See ALL your ratings</li>
                <li>✅ Upload after rating just {subscriptionTiers.premium.songsToRateBeforeUpload} songs</li>
              </ul>
            </div>
          </div>

          <div className="price-section">
            <p className="price">Only {subscriptionTiers.premium.monthlyPrice}/month</p>
            <button
              className="subscribe-button"
              onClick={handleSubscribe}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Subscribe Now'}
            </button>
          </div>
        </div>
      ) : (
        <div className="premium-tier-section">
          <h3>Premium Benefits</h3>

          <div className="benefits-list">
            <p>✅ Rate songs after {subscriptionConfig?.listeningTimeRequired} seconds</p>
            <p>✅ See all your ratings (unlimited)</p>
            <p>✅ Upload songs after rating just {subscriptionConfig?.songsToRateBeforeUpload} songs</p>
          </div>

          {subscriptionStatus === 'past_due' && (
            <div className="payment-warning">
              ⚠️ Your payment is past due. Please update your payment method to continue enjoying premium features.
            </div>
          )}

          <button
            className="manage-button"
            onClick={handleManageSubscription}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Manage Subscription'}
          </button>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
}

export default SubscriptionManager;